import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../lib/firebase";
import { Play, MapPin, Music, Globe, Instagram, Twitter, Youtube, ChevronLeft, Radio } from "lucide-react";

const SOCIAL_ICONS = {
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
};

export default function CreatorProfile() {
  const { slug } = useParams();
  const [creator, setCreator] = useState(null);
  const [content, setContent] = useState([]);
  const [station, setStation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCreator();
  }, [slug]);

  const loadCreator = async () => {
    try {
      // Find creator by slug
      const q = query(collection(db, "creators"), where("slug", "==", slug), where("status", "==", "approved"));
      const snap = await getDocs(q);
      if (snap.empty) { setLoading(false); return; }
      const creatorData = { id: snap.docs[0].id, ...snap.docs[0].data() };
      setCreator(creatorData);

      // Load content
      const contentQ = query(collection(db, "content"), where("creatorId", "==", creatorData.id), where("status", "==", "approved"), orderBy("createdAt", "desc"));
      const contentSnap = await getDocs(contentQ);
      setContent(contentSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      // Load station
      const stationQ = query(collection(db, "stations"), where("creatorId", "==", creatorData.id), where("status", "==", "approved"));
      const stationSnap = await getDocs(stationQ);
      if (!stationSnap.empty) setStation({ id: stationSnap.docs[0].id, ...stationSnap.docs[0].data() });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#00ff77] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!creator) return (
    <div className="text-center py-20">
      <p className="text-zinc-500">Creator not found.</p>
      <Link to="/explore" className="text-[#00ff77] text-sm mt-2 inline-block">← Explore Creators</Link>
    </div>
  );

  const socials = creator.socials || {};

  return (
    <div data-testid="creator-profile" className="space-y-8 animate-fade-in">
      {/* Cover */}
      <div className="relative rounded-2xl overflow-hidden h-40 sm:h-56 bg-[#111]">
        {creator.coverURL && (
          <img src={creator.coverURL} alt="" className="w-full h-full object-cover opacity-60" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
      </div>

      {/* Profile info */}
      <div className="-mt-16 relative z-10 px-4 flex flex-col sm:flex-row items-start sm:items-end gap-4">
        <div className="w-24 h-24 rounded-2xl border-4 border-[#0a0a0a] bg-[#111] overflow-hidden flex-shrink-0">
          {creator.photoURL ? (
            <img src={creator.photoURL} alt={creator.displayName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[#00ff77]/10">
              <span className="text-[#00ff77] text-2xl font-black">{creator.displayName?.[0] || "C"}</span>
            </div>
          )}
        </div>
        <div className="flex-1 pb-1">
          <h1 className="text-2xl font-black text-white">{creator.displayName}</h1>
          {creator.location && (
            <p className="text-zinc-500 text-sm flex items-center gap-1 mt-0.5">
              <MapPin size={12} /> {creator.location}
            </p>
          )}
          {creator.genres && creator.genres.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {creator.genres.map((g) => (
                <span key={g} className="text-xs bg-[#00ff77]/10 text-[#00ff77] px-2 py-0.5 rounded-full">{g}</span>
              ))}
            </div>
          )}
        </div>

        {/* Socials / EPK */}
        <div className="flex items-center gap-2">
          {Object.entries(socials).filter(([, v]) => v).map(([key, url]) => {
            const Icon = SOCIAL_ICONS[key] || Globe;
            return (
              <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors">
                <Icon size={14} />
              </a>
            );
          })}
          {creator.epkURL && (
            <a href={creator.epkURL} target="_blank" rel="noopener noreferrer"
              data-testid="epk-link"
              className="text-xs bg-[#00ff77] text-black font-bold px-3 py-1.5 rounded-full hover:bg-[#00e66b] transition-colors">
              EPK
            </a>
          )}
        </div>
      </div>

      {/* Bio */}
      {creator.bio && (
        <div className="bg-[#111] border border-white/5 rounded-2xl p-5">
          <p className="text-zinc-300 text-sm leading-relaxed">{creator.bio}</p>
        </div>
      )}

      {/* Station */}
      {station && (
        <section>
          <h2 className="text-base font-bold text-white mb-3 flex items-center gap-2">
            <Radio size={16} className="text-[#B026FF]" /> Radio Station
          </h2>
          <Link to={`/radio/${station.id}`} data-testid="creator-station-link"
            className="flex items-center gap-4 bg-[#111] border border-white/5 rounded-2xl p-4 hover:border-[#B026FF]/30 transition-all">
            <div className="w-12 h-12 rounded-xl bg-[#1a1a1a] overflow-hidden">
              {station.logoURL && <img src={station.logoURL} alt="" className="w-full h-full object-cover" />}
            </div>
            <div>
              <p className="text-white font-semibold text-sm">{station.name}</p>
              <p className="text-zinc-500 text-xs">{station.genre}</p>
            </div>
          </Link>
        </section>
      )}

      {/* Content */}
      <section>
        <h2 className="text-base font-bold text-white mb-4">Content ({content.length})</h2>
        {content.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {content.map((item) => (
              <Link key={item.id} to={`/content/${item.id}`} data-testid={`creator-content-${item.id}`}
                className="group bg-[#111] border border-white/5 rounded-2xl overflow-hidden hover:bg-[#1a1a1a] hover:border-[#00ff77]/30 transition-all duration-300">
                <div className="relative aspect-video bg-[#1a1a1a]">
                  {item.thumbnailURL ? (
                    <img src={item.thumbnailURL} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Play size={24} className="text-zinc-600" /></div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="text-white text-sm font-semibold line-clamp-2">{item.title}</h3>
                  <p className="text-zinc-600 text-xs mt-1">{item.views || 0} views</p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-10 text-zinc-600 text-sm">No published content yet.</div>
        )}
      </section>
    </div>
  );
}
